import React, { useState, useMemo } from 'react';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import BaseGrid from './BaseGrid';

// ─── Backend response tipleri ────────────────────────────────────────────────

export interface ApiRound {
  round: number;
  step: string;
  actor: 'buyer' | 'supplier';
  actorName: string;
  date: string;
  quantity?: number | null;
  price?: number | null;
  currency: string;
  priceUsd?: number | null;
  deliveryDays?: number | null;
  note?: string | null;
  vatRate?: number | null;
  vatIncluded?: boolean | null;
  paymentTerm?: string | null;
  validUntil?: number | null;
  decisionStatus?: string | null;
  emailSentAt?: string | null;
  emailOpenedAt?: string | null;
  respondedAt?: string | null;
}

export type QuoteStatus = 'Awarded' | 'Negotiating' | 'Responded' | 'Rejected' | 'Pending';

export interface ApiQuote {
  supplierId: number;
  supplierName: string;
  paymentTerm?: string | null;
  quoteStatus: QuoteStatus;
  vatIncluded: boolean;
  vatRate?: number | null;
  validUntilDate?: string | null;
  isExpired: boolean;
  rounds: ApiRound[];
}

// ─── Format helpers ───────────────────────────────────────────────────────────

export function fmtDate(iso: string | null | undefined, withTime = true): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  if (!withTime) return `${day}.${month}.${year}`;
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${h}:${m}`;
}

export function fmtMoney(n: number | null | undefined, currency = 'TRY', digits = 4): string {
  if (n == null || isNaN(Number(n))) return '—';
  const s = Number(n).toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const sym = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '₺';
  return `${sym} ${s}`;
}

export function fmtNum(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('tr-TR');
}

// ─── Tedarikçi liste satır türetme ───────────────────────────────────────────

export interface QuoteRow {
  data: ApiQuote;
  lastPrice: number | null;
  lastCurrency: string;
  lastPriceUsd: number | null;
}

export function buildQuoteRow(s: ApiQuote): QuoteRow {
  const supplierRounds = s.rounds.filter(r => r.actor === 'supplier');
  const last = supplierRounds[supplierRounds.length - 1];
  return {
    data: s,
    lastPrice: last?.price ?? null,
    lastCurrency: last?.currency ?? 'TRY',
    lastPriceUsd: last?.priceUsd ?? null,
  };
}

// ─── Tur tablosu (AG Grid) ───────────────────────────────────────────────────

export const ROUNDS_COL_DEFS: ColDef<ApiRound>[] = [
  {
    field: 'round',
    headerName: 'Tur',
    flex: 0.35,
    minWidth: 50,
    valueFormatter: p => (p.value != null ? `${p.value}.tur` : ''),
  },
  {
    colId: 'step',
    headerName: 'Adım',
    flex: 1.4,
    minWidth: 160,
    valueGetter: p => {
      if (!p.data) return '';
      const { actor, step } = p.data;
      if (actor === 'supplier') return 'Tedarikçi cevap verdi';
      switch (step) {
        case 'Teklif Talebi Gönderildi':
          return 'Alıcı teklif talebi gönderdi';
        case 'Revize Teklif Gönderildi':
          return 'Alıcı revize teklifi gönderdi';
        case 'Revizyon Talebi':
          return 'Alıcı revizyon talebi gönderdi';
        case 'Kazanan Seçildi':
          return 'Alıcı kazananı seçti';
        default:
          return step;
      }
    },
  },
  {
    field: 'date',
    headerName: 'Tarih',
    flex: 0.9,
    minWidth: 115,
    valueFormatter: p => fmtDate(p.value),
  },
  {
    field: 'quantity',
    headerName: 'Miktar',
    flex: 0.65,
    minWidth: 72,
    type: 'rightAligned',
    valueFormatter: p => fmtNum(p.value),
  },
  {
    colId: 'price',
    headerName: 'Birim Fiyat',
    flex: 1.1,
    minWidth: 130,
    type: 'rightAligned',
    cellRenderer: (p: ICellRendererParams<ApiRound>) => {
      if (!p.data) return null;
      const formatted = fmtMoney(p.data.price, p.data.currency);
      const usd = p.data.priceUsd != null ? fmtMoney(p.data.priceUsd, 'USD') : null;
      return (
        <span>
          {formatted}
          {usd && <span className="text-gray-400 ml-1 text-[10px]">≈ {usd}</span>}
        </span>
      );
    },
  },
  {
    field: 'deliveryDays',
    headerName: 'Teslim',
    flex: 0.7,
    minWidth: 70,
    type: 'rightAligned',
    valueFormatter: p => (p.value != null ? `${p.value} gün` : '—'),
  },
  {
    colId: 'vat',
    headerName: 'KDV',
    flex: 0.9,
    minWidth: 100,
    type: 'rightAligned',
    valueGetter: p => {
      if (p.data?.vatRate == null) return null;
      return p.data.vatRate;
    },
    cellRenderer: (p: ICellRendererParams<ApiRound>) => {
      if (p.data?.vatRate == null) return <span className="text-gray-400">—</span>;
      const included = p.data.vatIncluded;
      return (
        <span className="inline-flex items-center gap-1">
          <span>%{p.data.vatRate}</span>
          {included != null && (
            <span
              className={`text-[10px] px-1 rounded ${
                included
                  ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              }`}
              title={included ? 'KDV dahil' : 'KDV hariç'}
            >
              {included ? 'dahil' : 'hariç'}
            </span>
          )}
        </span>
      );
    },
  },
  {
    colId: 'total',
    headerName: 'Toplam',
    flex: 1,
    minWidth: 100,
    type: 'rightAligned',
    valueGetter: p => {
      const d = p.data;
      if (d?.quantity == null || d?.price == null) return null;
      const net = d.quantity * d.price;
      if (d.vatIncluded || d.vatRate == null) return net;
      return net * (1 + Number(d.vatRate) / 100);
    },
    valueFormatter: p => {
      if (p.value == null) return '—';
      const currency = (p.data as ApiRound | undefined)?.currency ?? 'TRY';
      return fmtMoney(p.value as number, currency, 0);
    },
  },
  {
    field: 'paymentTerm',
    headerName: 'Ödeme Şekli',
    flex: 1.2,
    minWidth: 130,
    hide: true,
    valueFormatter: p => p.value ?? '—',
  },
  {
    field: 'validUntil',
    headerName: 'Geçerlilik',
    flex: 0.8,
    minWidth: 95,
    hide: true,
    type: 'rightAligned',
    valueFormatter: p => (p.value != null ? `${p.value} gün` : '—'),
  },
  {
    field: 'decisionStatus',
    headerName: 'Karar',
    flex: 0.9,
    minWidth: 110,
    valueFormatter: p => {
      switch (p.value) {
        case 'Awarded':
          return 'Kazanan';
        case 'Negotiate':
          return 'Müzakere';
        case 'Rejected':
          return 'Reddedildi';
        default:
          return '—';
      }
    },
    cellStyle: p => {
      if (p.value === 'Awarded') return { color: '#16a34a', fontWeight: 600 };
      if (p.value === 'Rejected') return { color: '#dc2626' };
      return null;
    },
  },
  {
    field: 'note',
    headerName: 'Ted. Notu',
    flex: 1.5,
    minWidth: 150,
    hide: true,
    valueFormatter: p => p.value ?? '—',
  },
];

export const ALL_COLUMNS: { colId: string; label: string }[] = [
  { colId: 'round', label: 'Tur' },
  { colId: 'step', label: 'Adım' },
  { colId: 'date', label: 'Tarih' },
  { colId: 'quantity', label: 'Miktar' },
  { colId: 'price', label: 'Birim Fiyat' },
  { colId: 'deliveryDays', label: 'Teslim' },
  { colId: 'vat', label: 'KDV' },
  { colId: 'total', label: 'Toplam' },
  { colId: 'paymentTerm', label: 'Ödeme Şekli' },
  { colId: 'validUntil', label: 'Geçerlilik' },
  { colId: 'decisionStatus', label: 'Karar' },
  { colId: 'note', label: 'Ted. Notu' },
];

export const INITIALLY_HIDDEN = new Set(['paymentTerm', 'validUntil', 'decisionStatus', 'note']);

interface DetailRowMarker {
  __detail: true;
}
type RoundsRow = ApiRound | (ApiRound & DetailRowMarker);

function isDetailRow(r: RoundsRow | undefined | null): r is ApiRound & DetailRowMarker {
  return !!r && (r as Partial<DetailRowMarker>).__detail === true;
}

export const RoundTimeline: React.FC<{ data: ApiRound }> = ({ data }) => {
  const stages = [
    { label: 'Teklif gönderildi', time: data.emailSentAt },
    { label: 'Teklif açıldı', time: data.emailOpenedAt },
    { label: 'Teklif cevaplandı', time: data.respondedAt },
  ];
  return (
    <div className="px-10 py-4 bg-white dark:bg-gray-900 h-full flex items-center">
      <div className="flex items-start w-full max-w-2xl mx-auto">
        {stages.map((s, idx) => {
          const done = !!s.time;
          const prevDone = idx > 0 ? !!stages[idx - 1]?.time : true;
          return (
            <React.Fragment key={s.label}>
              {idx > 0 && (
                <div
                  className={`flex-1 h-0.5 mt-3.5 ${
                    prevDone && done ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
              <div className="flex flex-col items-center min-w-[110px]">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                    done
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {done ? '✓' : idx + 1}
                </div>
                <div
                  className={`mt-1.5 text-xs ${
                    done ? 'text-gray-700 dark:text-gray-200 font-medium' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {s.label}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">{s.time ? fmtDate(s.time) : '—'}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const RoundsGrid: React.FC<{
  rounds: ApiRound[];
  hiddenColumns: Set<string>;
  gridStateKey: string;
}> = ({ rounds, hiddenColumns, gridStateKey }) => {
  const headerH = 36;
  const rowH = 36;
  const detailH = 110;
  const hScrollBarH = 17;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const rowData = useMemo<RoundsRow[]>(() => {
    const out: RoundsRow[] = [];
    for (const r of rounds) {
      out.push(r);
      if (expanded.has(r.round)) out.push({ ...r, __detail: true });
    }
    return out;
  }, [rounds, expanded]);

  const height =
    headerH +
    rounds.length * rowH +
    rounds.reduce((sum, r) => sum + (expanded.has(r.round) ? detailH : 0), 0) +
    hScrollBarH;

  const colDefs = useMemo<ColDef<ApiRound>[]>(
    () =>
      ROUNDS_COL_DEFS.map(col => {
        const id = (col.colId ?? (col as { field?: string }).field) as string | undefined;
        if (!id) return col;
        return { ...col, hide: hiddenColumns.has(id) };
      }),
    [hiddenColumns],
  );

  return (
    <div className="px-6 py-2 bg-gray-50 dark:bg-gray-800/60" style={{ height: height + 16 }}>
      <BaseGrid<ApiRound>
        gridStateKey={gridStateKey}
        rowData={rowData as ApiRound[]}
        columnDefs={colDefs}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          floatingFilter: false,
        }}
        domLayout="normal"
        rowHeight={rowH}
        headerHeight={headerH}
        onCellMouseOver={event => {
          const target = event.event?.target as HTMLElement | null;
          const cell = target?.closest('.ag-cell') as HTMLElement | null;
          if (!cell) return;
          const inner = (cell.querySelector('.ag-cell-value') as HTMLElement | null) ?? cell;
          if (inner.scrollWidth > inner.clientWidth + 1) {
            const text = event.value != null ? String(event.value) : inner.innerText ?? '';
            if (text) cell.setAttribute('title', text);
          } else {
            cell.removeAttribute('title');
          }
        }}
        isFullWidthRow={p => isDetailRow(p.rowNode.data as RoundsRow | undefined)}
        fullWidthCellRenderer={(p: ICellRendererParams<ApiRound>) =>
          p.data ? <RoundTimeline data={p.data} /> : null
        }
        getRowHeight={p => (isDetailRow(p.data as RoundsRow | undefined) ? detailH : rowH)}
        getRowClass={p => (isDetailRow(p.data as RoundsRow | undefined) ? '' : 'cursor-pointer')}
        onRowClicked={p => {
          const d = p.data as RoundsRow | undefined;
          if (!d || isDetailRow(d)) return;
          setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(d.round)) next.delete(d.round);
            else next.add(d.round);
            return next;
          });
        }}
      />
    </div>
  );
};

// ─── Tedarikçi listesi (master) ──────────────────────────────────────────────

export const SupplierList: React.FC<{
  quotes: ApiQuote[];
  hiddenColumns: Set<string>;
  gridStateKey: string;
}> = ({ quotes, hiddenColumns, gridStateKey }) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const supplierRows = useMemo(() => {
    const rows = quotes.map(buildQuoteRow);
    rows.sort((a, b) => {
      const aw = a.data.quoteStatus === 'Awarded' ? -1 : 0;
      const bw = b.data.quoteStatus === 'Awarded' ? -1 : 0;
      return aw - bw;
    });
    return rows;
  }, [quotes]);

  const toggle = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (supplierRows.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
        Bu talep için henüz teklif bulunmuyor.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-300">
        <div className="flex-[2] min-w-[200px]">Tedarikçi</div>
        <div className="flex-[1.2] min-w-[140px] text-right">Son Fiyat</div>
      </div>
      {supplierRows.map(row => {
        const s = row.data;
        const isExpanded = expandedIds.has(s.supplierId);
        const isAwarded = s.quoteStatus === 'Awarded';
        const isRejected = s.quoteStatus === 'Rejected';
        const usd = row.lastPriceUsd != null ? fmtMoney(row.lastPriceUsd, 'USD') : null;
        return (
          <React.Fragment key={s.supplierId}>
            <div
              onClick={() => toggle(s.supplierId)}
              className={`flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-700 cursor-pointer text-sm transition-colors
                ${isAwarded ? 'bg-green-50 dark:bg-green-900/20' : ''}
                ${isRejected ? 'opacity-70' : ''}
                hover:bg-gray-50 dark:hover:bg-gray-800/60`}
            >
              <div className="flex-[2] min-w-[200px] flex items-center gap-1.5">
                <span className="text-gray-400 w-3 inline-block">{isExpanded ? '▾' : '▸'}</span>
                {isAwarded && <i className="fas fa-trophy text-yellow-500 text-xs" title="Kazanan" />}
                <span className={isAwarded ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}>
                  {s.supplierName}
                </span>
              </div>
              <div className="flex-[1.2] min-w-[140px] text-right text-gray-900 dark:text-white">
                {fmtMoney(row.lastPrice, row.lastCurrency)}
                {usd && <span className="text-gray-400 ml-1 text-[10px]">≈ {usd}</span>}
              </div>
            </div>
            {isExpanded && (
              <RoundsGrid
                rounds={s.rounds}
                hiddenColumns={hiddenColumns}
                gridStateKey={`${gridStateKey}-supplier-${s.supplierId}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
